import {FACTIONS} from "../../../constants/factions";
import {NEUTRAL_PLAYER_ID} from "../../../constants/players";
import {GIANTS_D20_SAME_TYPE_UNIT_LIMIT, GIANTS_UNIT_MAX_SIZE} from "../../../configs/factions/giants";
import {
  W39_GIANTS_BARRACKS_HOLD_SHARE,
  W39_GIANTS_BARRACKS_HOLD_SHARE_CAMP,
  W39_GIANTS_BARRACKS_STAY_PER_FARM,
  W39_GIANTS_ENGAGE_RATIO,
  W39_GIANTS_FARMS_BASE,
  W39_GIANTS_FARMS_MAX,
  W39_GIANTS_FARMS_PER_TWO_ROUNDS,
  W39_GIANTS_GROWTH_TARGET_SIZE,
  W39_GIANTS_NEXT_HIRE_MIN_SIZE,
  W39_GIANTS_RECEIVED_DAMAGE_WEIGHT,
  W39_GIANTS_YOUNG_SIZE,
  W39_GIANTS_YOUNG_THREAT_DISTANCE,
  W39_GIANTS_YOUNG_THREAT_PENALTY,
  W39_GIANTS_YOUNG_THREAT_SIZE_SHARE,
} from "../../../configs/botW39Factions";
import {
  W40_GIANTS_ATTACK_ENGAGE_RATIO,
  W40_GIANTS_ATTACK_MIN_SIZE,
  W40_GIANTS_ATTACK_PULL_MIN_SIZE,
  W40_GIANTS_ATTACK_PULL_PER_CELL,
  W40_GIANTS_ATTACK_PULL_RANGE,
  W40_GIANTS_ATTACK_RECEIVED_DAMAGE_WEIGHT,
} from "../../../configs/botW40Factions";
import {isPreW40V3Player} from "../abTestGates";
import {getHexDistance} from "../../coords/getHexDistance";
import {getUnitPrice, UNIT_TYPES} from "../../../configs/units";
import {checkIfW39FactionPlay, checkIfW39FactionUnit} from "./w39FactionPlay";

// Великаны волны 39 (developer_artifacts/bot_strategy/faction_strategy/giants.md)

export const checkIfW39GiantsPlayer = (player) => checkIfW39FactionPlay(player, FACTIONS.GIANTS);

export const checkIfW39GiantUnit = (unit) => checkIfW39FactionUnit(unit, FACTIONS.GIANTS);

/**
 * Предел размера отряда для решений ботов о росте (удержание на казарме,
 * докупка размера). Общий код берет maxSize из конфига ТИПА — у великана это
 * 20 (d20), хотя по правилам фракции он растет до 99 (unit.maxSize). Великану
 * волны 39 — 40: выше расти бессмысленно, отряды противников не больше 20.
 * У остальных отрядов (и у великанов legacy-игроков) — typeMaxSize как есть
 */
export const getW39GrowthMaxSize = (unit, typeMaxSize) => (
  checkIfW39GiantUnit(unit)
    ? Math.min(unit.maxSize || GIANTS_UNIT_MAX_SIZE, W39_GIANTS_GROWTH_TARGET_SIZE)
    : typeMaxSize
);

/**
 * Можно ли нанять еще одного великана. Первый — всегда. Следующий — когда
 * каждый уже нанятый дорос до W39_GIANTS_NEXT_HIRE_MIN_SIZE, и великанов меньше,
 * чем городов + 1 (третий и четвертый — под удержание захваченных городов), и
 * меньше лимита правил
 */
export const checkIfW39GiantsHireAllowed = (playerUnits, ownCitiesCount) => {
  const giants = playerUnits.filter((unit) => unit.factionId === FACTIONS.GIANTS);
  if (giants.length === 0) {
    return true;
  }
  if (giants.length >= Math.min(GIANTS_D20_SAME_TYPE_UNIT_LIMIT, ownCitiesCount + 1)) {
    return false;
  }
  return giants.every((unit) => unit.size >= W39_GIANTS_NEXT_HIRE_MIN_SIZE);
};

/**
 * Резерв монет стройки под найм великана. Общий резерв ботов — цена
 * предпочтительного типа с потолком 8 (d10/d8), а великан стоит 10: без своего
 * резерва стройка и политика съедали монеты, и первый великан появлялся только
 * к 7–10 раунду (пилот волны 39). Великан нанимается, когда это разрешает
 * checkIfW39GiantsHireAllowed; без казармы — прежний минимальный резерв
 * (казарму надо построить), в последнем раунде найма нет — 0
 */
export const calcW39GiantsHireCoinReserve = ({player, playerUnits, citiesCount, hasBarracks, isFinalRound,
  minReserve}) => {
  if (isFinalRound) {
    return 0;
  }
  if (!hasBarracks) {
    return playerUnits.length === 0 ? minReserve : 0;
  }
  return checkIfW39GiantsHireAllowed(playerUnits, citiesCount) ? getUnitPrice(UNIT_TYPES.D20.id, player).price : 0;
};

/**
 * Резерв монет политики под ПЕРВОГО великана: политика покупается до построек
 * и раньше всех тратит монеты дебюта. Пока великана нет (и раунд не последний),
 * на политику идут только монеты сверх его цены
 */
export const calcW39GiantsPolicyCoinReserve = ({player, playerUnits, isFinalRound}) => (
  checkIfW39GiantsPlayer(player) && !isFinalRound && playerUnits.length === 0
    ? getUnitPrice(UNIT_TYPES.D20.id, player).price
    : 0
);

/** Волна 40 (V3): идет ли игрок великанами в бой и к чужим городам */
export const checkIfW40GiantsAttackPlayer = (player) => (
  checkIfW39GiantsPlayer(player) && !isPreW40V3Player(player.id)
);

const checkIfW40AttackingGiant = (unit) => !isPreW40V3Player(unit.ownerId) && unit.size >= W40_GIANTS_ATTACK_MIN_SIZE;

/** Вес урона, полученного великаном, в оценке позиции */
export const getW39GiantReceivedDamageWeight = (unit) => (
  checkIfW40AttackingGiant(unit) ? W40_GIANTS_ATTACK_RECEIVED_DAMAGE_WEIGHT : W39_GIANTS_RECEIVED_DAMAGE_WEIGHT
);

/** Цель числа ферм великанов в раунде round (больше, чем у остальных фракций) */
export const getW39GiantsFarmsTarget = (round) => Math.min(
  W39_GIANTS_FARMS_MAX,
  W39_GIANTS_FARMS_BASE + Math.floor((Math.max(1, round) - 1) / 2) * W39_GIANTS_FARMS_PER_TWO_ROUNDS
);

/**
 * Поправка оценки позиции великана в перемещении:
 *  - ближний бой только от превосходства: размер не меньше суммы смежных
 *    врагов × W39_GIANTS_ENGAGE_RATIO; иначе штраф пропорционально нехватке
 *    (на квартале своего города — без штрафа: оборона, на чужом — половина);
 *  - молодой великан (размер < W39_GIANTS_YOUNG_SIZE) не встает в досягаемость
 *    отрядов игроков-противников сопоставимого размера (не на кварталах своих
 *    городов — там оборона);
 *  - своя рабочая казарма удваивает рост от ферм: надбавка за каждую свою ферму,
 *    пока великан ниже порога удержания (с лагерем у казармы порог выше).
 *
 * adjacentEnemies — чужие отряды, смежные клетке; suicidePenalty — штраф
 * самоубийственной позиции общей оценки (масштаб); isOwnBarracks /
 * isCampAdjacent — клетка своя казарма и смежна ли лагерю; ownFarmsCount —
 * свои фермы
 */
export const scoreW39GiantPosition = ({
  unit, cellKey, units, adjacentEnemies, isOwnQuarter, isEnemyQuarter, suicidePenalty,
  isOwnBarracks = false, isCampAdjacent = false, ownFarmsCount = 0, attackDistance,
}) => {
  let score = 0;

  if (isOwnBarracks && adjacentEnemies.length === 0) {
    const holdSize = getW39GrowthMaxSize(unit, unit.maxSize || GIANTS_UNIT_MAX_SIZE)
      * (isCampAdjacent ? W39_GIANTS_BARRACKS_HOLD_SHARE_CAMP : W39_GIANTS_BARRACKS_HOLD_SHARE);
    if (unit.size < holdSize) {
      score += ownFarmsCount * W39_GIANTS_BARRACKS_STAY_PER_FARM;
    }
  }

  if (adjacentEnemies.length > 0 && !isOwnQuarter) {
    const enemiesTotalSize = adjacentEnemies.reduce((sum, enemy) => sum + enemy.size, 0);
    const requiredSize = enemiesTotalSize
      * (checkIfW40AttackingGiant(unit) ? W40_GIANTS_ATTACK_ENGAGE_RATIO : W39_GIANTS_ENGAGE_RATIO);
    if (unit.size < requiredSize) {
      const shortage = Math.min(1, (requiredSize - unit.size) / requiredSize);
      score -= suicidePenalty * shortage * (isEnemyQuarter ? 0.5 : 1);
    }
  }

  // Волна 40 (V3): выросший великан тянется к ближайшему чужому городу
  if (attackDistance !== undefined && unit.size >= W40_GIANTS_ATTACK_PULL_MIN_SIZE && !isPreW40V3Player(unit.ownerId)) {
    score += Math.max(0, W40_GIANTS_ATTACK_PULL_RANGE - attackDistance) * W40_GIANTS_ATTACK_PULL_PER_CELL;
  }

  if (unit.size < W39_GIANTS_YOUNG_SIZE && !isOwnQuarter) {
    const threatsCount = units.filter((otherUnit) => otherUnit.ownerId !== unit.ownerId
      && otherUnit.ownerId !== NEUTRAL_PLAYER_ID
      && otherUnit.size >= unit.size * W39_GIANTS_YOUNG_THREAT_SIZE_SHARE
      && getHexDistance(otherUnit.hexKey, cellKey) <= W39_GIANTS_YOUNG_THREAT_DISTANCE
    ).length;
    score -= threatsCount * W39_GIANTS_YOUNG_THREAT_PENALTY;
  }

  return score;
};
